// Spec ref: Partecipazione Detail.html mockup (2026-05-24)
// Detail layout: crumb-row + hero (PA badge + h1 cliente→lotto + lot-sub +
// stato + sposta stato + ⋯), anchor-nav 5 voci, detail-grid 1fr/320px,
// 6 sezioni main (Riepilogo split 2-col + asta-strip / Checklist /
// Documenti / Vault / Esito post-asta / Storico), rail 4 card
// (Countdown / Numeri chiave / Consulente / Cronologia top 5).

import { useEffect, useRef, useState } from 'react'
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from '@tanstack/react-router'
import {
  useAddChecklistItem,
  useDeleteChecklistItem,
  useParticipation,
  useParticipationActivity,
  usePatchParticipation,
  usePatchPostAggStep,
  usePostAggiudicazione,
  useToggleChecklistItem,
} from '../hooks/useParticipation'
import {
  useCreateVaultEntry,
  useDeleteVaultEntry,
  useGetVaultSecret,
  useUnlockVault,
  useUpdateVaultEntry,
  useVaultList,
} from '../hooks/useVault'
import {
  useDocumentList,
  useFetchDocumentUrl,
  useUploadDocument,
} from '../hooks/useDocuments'
import { useMe } from '../hooks/useAuth'
import { useUsersList } from '../hooks/useComments'
import { hasTokens } from '../lib/auth'
import { canDeleteVault, isReadOnly } from '../lib/permissions'
import { CardSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import { CommentsButton } from '../components/CommentsButton'
import { SendMessageModal } from '../components/SendMessageModal'
import { NewChatModal } from '../components/NewChatModal'
import { ProfessionalPicker } from '../components/ProfessionalPicker'
import type {
  ActivityEvent,
  ChecklistItem,
  DocumentKind,
  DocumentRow,
  ParticipationDetail,
  ParticipationPatch,
  PostAggStep,
  PostAggStepStatus,
  VaultCreate,
  VaultEntry,
} from '../lib/api'

export const Route = createFileRoute('/partecipazioni/$id')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: PartecipazioneDetailPage,
})

// =============================================================
// CONST + HELPERS
// =============================================================

const STATUS_LABEL: Record<string, string> = {
  draft: 'Bozza',
  checklist_open: 'Checklist',
  domanda_fatta: 'Domanda fatta',
  asta_in_corso: 'Asta in corso',
  aggiudicata: 'Aggiudicata',
  non_aggiudicata: 'Non aggiudicata',
  ritirata: 'Ritirata',
  contestata: 'Contestata',
  archivio: 'Archivio',
}

const STATUS_COLOR: Record<
  string,
  { bg: string; ink: string; border: string }
> = {
  draft: {
    bg: 'var(--bg-3, oklch(94% 0.006 250))',
    ink: 'var(--ink-3)',
    border: 'var(--ink-4)',
  },
  checklist_open: {
    bg: 'oklch(96% 0.05 80)',
    ink: 'oklch(45% 0.15 60)',
    border: 'var(--warn, #f59e0b)',
  },
  domanda_fatta: {
    bg: 'oklch(96% 0.03 35)',
    ink: 'oklch(40% 0.13 35)',
    border: 'var(--accent, #f97316)',
  },
  asta_in_corso: {
    bg: 'oklch(95% 0.04 200)',
    ink: 'var(--info-ink, #0369a1)',
    border: 'var(--info, #0ea5e9)',
  },
  aggiudicata: {
    bg: 'oklch(95% 0.04 145)',
    ink: 'var(--ok-ink, #15803d)',
    border: 'var(--ok, #16a34a)',
  },
  non_aggiudicata: {
    bg: 'oklch(96% 0.05 25)',
    ink: 'var(--risk-ink, #b91c1c)',
    border: 'var(--risk, #dc2626)',
  },
  ritirata: {
    bg: 'oklch(96% 0.05 25)',
    ink: 'var(--risk-ink, #b91c1c)',
    border: 'var(--risk, #dc2626)',
  },
  contestata: {
    bg: 'oklch(96% 0.05 80)',
    ink: 'oklch(45% 0.15 60)',
    border: 'var(--warn, #f59e0b)',
  },
  archivio: {
    bg: 'var(--bg-2)',
    ink: 'var(--ink-3)',
    border: 'var(--ink-4)',
  },
}

// TIPO_ASTA_OPTIONS rimosso: ora si vede tipo come chip read-only in hero.
// Cambio tipo via PATCH dedicato in futura modale "Modifica partecipazione".

const VAULT_KIND_OPTIONS = [
  'pec',
  'firma_digitale',
  'password_portale',
  'bonifico_ref',
  'altro',
] as const
type VaultKind = (typeof VAULT_KIND_OPTIONS)[number]

const DOCUMENT_KIND_OPTIONS: DocumentKind[] = [
  'ctu',
  'perizia',
  'planimetria',
  'avviso_vendita',
  'ordinanza',
  'bonifico_cauzione',
  'domanda_partecipazione',
  'documento_identita',
  'codice_fiscale',
  'procura',
  'dichiarazione',
  'firma_digitale',
  'altro',
]

// Workflow: per ogni stato corrente, liste possibili transizioni.
// Contestata rimossa da UI (user richiesta). Stato resta nel DB per dati
// storici ma non selezionabile da workflow corrente.
const STATUS_TRANSITIONS: Record<string, string[]> = {
  draft: ['checklist_open', 'ritirata'],
  checklist_open: ['domanda_fatta', 'ritirata'],
  domanda_fatta: ['asta_in_corso', 'aggiudicata', 'non_aggiudicata', 'ritirata'],
  asta_in_corso: ['aggiudicata', 'non_aggiudicata', 'ritirata'],
  aggiudicata: ['archivio'],
  non_aggiudicata: ['archivio'],
  ritirata: ['archivio'],
  contestata: ['aggiudicata', 'non_aggiudicata', 'archivio'],
  archivio: [],
}

function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v)
}

function fmtMoneyShort(v: number | null | undefined): string {
  if (v == null) return '—'
  if (Math.abs(v) >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`
  if (Math.abs(v) >= 1000) return `€${(v / 1000).toFixed(0)}k`
  return `€${v}`
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function relativeFromNow(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  const min = Math.floor(diffMs / 60_000)
  if (min < 1) return 'ora'
  if (min < 60) return `${min} min fa`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return `${hrs}h fa`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}g fa`
  return fmtDate(iso)
}

function metaStr(meta: Record<string, unknown> | null, key: string): string {
  if (!meta) return ''
  const v = meta[key]
  if (v === null || v === undefined) return ''
  return String(v)
}

function describeEvent(e: ActivityEvent): string {
  switch (e.action) {
    case 'participation.created':
      return 'Partecipazione creata'
    case 'participation.status_changed':
      return `Status: ${metaStr(e.metadata, 'from')} → ${metaStr(e.metadata, 'to')}`
    case 'participation.contestazione_opened': {
      const note = metaStr(e.metadata, 'note')
      return note ? `Contestazione aperta — ${note}` : 'Contestazione aperta'
    }
    case 'participation.contestazione_closed':
      return 'Contestazione chiusa'
    case 'checklist.item_completed':
      return `Checklist completata: ${metaStr(e.metadata, 'label')}`
    case 'vault.created':
      return `Credenziale aggiunta: ${metaStr(e.metadata, 'label')}`
    case 'vault.unlocked':
      return `Credenziale sbloccata: ${metaStr(e.metadata, 'label')}`
    case 'vault.deleted':
      return `Credenziale eliminata: ${metaStr(e.metadata, 'label')}`
    default:
      return e.action
  }
}

function eventTone(action: string): 'ok' | 'accent' | 'warn' | 'info' | '' {
  if (action.endsWith('.created')) return 'info'
  if (action === 'checklist.item_completed') return 'ok'
  if (action === 'participation.contestazione_opened') return 'warn'
  if (action === 'participation.status_changed') return 'accent'
  if (action.startsWith('vault.')) return 'info'
  return ''
}

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  const hue = Math.abs(h) % 360
  return `oklch(58% 0.14 ${hue})`
}

function initials(name: string | null | undefined): string {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

// =============================================================
// MAIN COMPONENT
// =============================================================

function PartecipazioneDetailPage() {
  const { id } = Route.useParams()
  const router = useRouter()
  const { data, isPending, isError, error } = useParticipation(id)
  const { data: me } = useMe()
  const readOnly = isReadOnly(me)
  const patchMut = usePatchParticipation(id)
  const addChecklistMut = useAddChecklistItem(id)
  const toggleChecklistMut = useToggleChecklistItem(id)
  const deleteChecklistMut = useDeleteChecklistItem(id)
  const vaultListQuery = useVaultList({ participation_id: id })
  const unlockMut = useUnlockVault(id)
  const getSecretMut = useGetVaultSecret()
  const createVaultMut = useCreateVaultEntry()
  const deleteVaultMut = useDeleteVaultEntry(id)
  const updateVaultMut = useUpdateVaultEntry(id)
  const activityQuery = useParticipationActivity(id)
  const docsQuery = useDocumentList({ participation_id: id, page_size: 100 })
  // Post-aggiudicazione (Giulia iter): solo se status aggiudicata.
  const isAggiudicata = data?.status === 'aggiudicata'
  const postAggQuery = usePostAggiudicazione(id, isAggiudicata)
  const patchPostAggMut = usePatchPostAggStep(id)

  // ----- UI state -----
  const [newTaskLabel, setNewTaskLabel] = useState<string>('')
  const [showAddTask, setShowAddTask] = useState<boolean>(false)
  const [showMsgModal, setShowMsgModal] = useState<boolean>(false)
  const [chatOpen, setChatOpen] = useState<boolean>(false)
  const [showStatoMenu, setShowStatoMenu] = useState<boolean>(false)
  const [showMoreMenu, setShowMoreMenu] = useState<boolean>(false)
  // Form esito unificato: aggiudicata o non_aggiudicata (persa).
  // Prezzo obbligatorio in entrambi i casi (chiusura asta = prezzo finale
  // noto sempre, anche se cliente non ha vinto -> prezzo aggiudicatario).
  const [esitoMode, setEsitoMode] = useState<
    'aggiudicata' | 'non_aggiudicata' | null
  >(null)
  const [aggPrezzo, setAggPrezzo] = useState<string>('')
  const [aggData, setAggData] = useState<string>('')
  const [showVaultForm, setShowVaultForm] = useState<boolean>(false)
  const [editingVault, setEditingVault] = useState<VaultEntry | null>(null)

  // Vault form
  const [vLabel, setVLabel] = useState<string>('')
  const [vKind, setVKind] = useState<VaultKind>('pec')
  const [vSecret, setVSecret] = useState<string>('')
  const [vUsername, setVUsername] = useState<string>('')
  const [vNote, setVNote] = useState<string>('')
  const [vaultErr, setVaultErr] = useState<string>('')

  // Vault display countdown
  type DisplayedSecret = { plaintext: string; expiresAt: number }
  const [shown, setShown] = useState<Record<string, DisplayedSecret>>({})
  const [now, setNow] = useState<number>(() => Date.now())
  const tickRef = useRef<number | null>(null)

  useEffect(() => {
    if (Object.keys(shown).length === 0) {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current)
        tickRef.current = null
      }
      return
    }
    if (tickRef.current === null) {
      tickRef.current = window.setInterval(() => {
        setNow(Date.now())
      }, 1000)
    }
    return () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current)
        tickRef.current = null
      }
    }
  }, [shown])

  useEffect(() => {
    setShown((prev) => {
      let changed = false
      const next: Record<string, DisplayedSecret> = {}
      for (const [k, v] of Object.entries(prev)) {
        if (v.expiresAt > now) next[k] = v
        else changed = true
      }
      return changed ? next : prev
    })
  }, [now])

  // Close stato menu on outside click / Esc
  useEffect(() => {
    if (!showStatoMenu && !showMoreMenu) return
    function onDoc(e: MouseEvent) {
      const target = e.target as HTMLElement
      if (!target.closest('[data-stato-menu]') && !target.closest('[data-more-menu]')) {
        setShowStatoMenu(false)
        setShowMoreMenu(false)
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setShowStatoMenu(false)
        setShowMoreMenu(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [showStatoMenu, showMoreMenu])

  // ----- Loading + error states -----
  if (isPending) {
    return (
      <div>
        <CardSkeleton />
        <div style={{ height: 16 }} />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    )
  }
  if (isError || !data) {
    const notFound = error?.status === 404
    return (
      <div>
        <EmptyState
          icon={notFound ? '🔍' : '⚠️'}
          title={notFound ? 'Non trovato' : 'Errore'}
          body={
            notFound
              ? "L'elemento è stato eliminato o non hai i permessi per accederlo."
              : (error?.message ?? 'Impossibile caricare la partecipazione')
          }
        />
      </div>
    )
  }

  const p = data

  function patch(body: ParticipationPatch) {
    patchMut.mutate(body)
  }

  function onTransitionStatus(newStatus: string) {
    // Esiti finali asta: richiedi sempre prezzo + data (form).
    if (newStatus === 'aggiudicata' || newStatus === 'non_aggiudicata') {
      setEsitoMode(newStatus)
      setShowStatoMenu(false)
      return
    }
    patch({ status: newStatus })
    setShowStatoMenu(false)
  }

  function onSubmitEsito() {
    if (!esitoMode) return
    const prezzo = aggPrezzo ? Number(aggPrezzo) : undefined
    if (prezzo === undefined || !Number.isFinite(prezzo) || prezzo <= 0) return
    if (!aggData) return // data obbligatoria
    const body: ParticipationPatch = {
      status: esitoMode,
      // prezzo_aggiudicazione = prezzo a cui asta si è chiusa.
      // Anche per "persa": registriamo il prezzo aggiudicatario per analisi
      // (gap vs offerta cliente, trend zona, ecc).
      prezzo_aggiudicazione: prezzo,
      data_aggiudicazione: aggData,
    }
    patch(body)
    setEsitoMode(null)
    setAggPrezzo('')
    setAggData('')
  }

  function onAddChecklistItem() {
    const label = newTaskLabel.trim()
    if (!label) return
    addChecklistMut.mutate(label, {
      onSuccess: () => {
        setNewTaskLabel('')
        setShowAddTask(false)
      },
    })
  }

  async function onShowSecret(vaultId: string) {
    setVaultErr('')
    try {
      await unlockMut.mutateAsync(vaultId)
      const res = await getSecretMut.mutateAsync(vaultId)
      const expiresMs = new Date(res.expires_at).getTime()
      setShown((prev) => ({
        ...prev,
        [vaultId]: { plaintext: res.secret, expiresAt: expiresMs },
      }))
    } catch (err) {
      setVaultErr(err instanceof Error ? err.message : 'Errore unlock vault')
    }
  }

  function onHideSecret(vaultId: string) {
    setShown((prev) => {
      const next = { ...prev }
      delete next[vaultId]
      return next
    })
  }

  function onDeleteVault(entry: VaultEntry) {
    const ok = window.confirm(
      `Eliminare la credenziale "${entry.label}"? L'azione è irreversibile.`,
    )
    if (!ok) return
    deleteVaultMut.mutate(entry.id, {
      onSuccess: () => onHideSecret(entry.id),
    })
  }

  function onSubmitVault(e: React.FormEvent) {
    e.preventDefault()
    setVaultErr('')
    if (!vLabel.trim() || !vSecret) {
      setVaultErr('Label e secret obbligatori')
      return
    }
    const body: VaultCreate = {
      label: vLabel.trim(),
      kind: vKind,
      secret: vSecret,
      participation_id: id,
    }
    if (vUsername.trim()) body.username = vUsername.trim()
    if (vNote.trim()) body.note = vNote.trim()
    createVaultMut.mutate(body, {
      onSuccess: () => {
        setVLabel('')
        setVKind('pec')
        setVSecret('')
        setVUsername('')
        setVNote('')
        setShowVaultForm(false)
      },
      onError: (err) => setVaultErr(err.message),
    })
  }

  // ----- Derived -----
  const pCode = `PA_${String(p.short_code).padStart(5, '0')}`
  const cCode = `C_${String(p.client_short_code).padStart(5, '0')}`
  const lCode = `L_${String(p.property_short_code).padStart(5, '0')}`
  const statusLabel = STATUS_LABEL[p.status] ?? p.status
  const statusColor = STATUS_COLOR[p.status] ?? STATUS_COLOR.draft
  const transitions = STATUS_TRANSITIONS[p.status] ?? []
  const checklistPct =
    p.checklist_total > 0
      ? Math.round((p.checklist_done / p.checklist_total) * 100)
      : 0
  const docs = docsQuery.data?.items ?? []
  const totalSize = docs.reduce((sum, d) => sum + (d.size_bytes ?? 0), 0)
  const lastActivity = activityQuery.data?.[0]
  const isPastAuction =
    p.property_data_asta != null &&
    new Date(p.property_data_asta).getTime() < Date.now()

  return (
    <>
      {/* === CRUMB-ROW + back top === */}
      <div className="crumb-row">
        <div
          className="breadcrumb"
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}
        >
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              if (window.history.length > 1) router.history.back()
              else
                void router.navigate({
                  to: '/partecipazioni',
                  search: {
                    page: 1,
                    page_size: 50,
                    q: '',
                    sort: '-created_at',
                  },
                })
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
            }}
            title="Torna indietro"
          >
            ← Indietro
          </button>
          <span>Operativo</span>
          <span className="sep">/</span>
          <Link
            to="/partecipazioni"
            search={{
              page: 1,
              page_size: 50,
              q: '',
              sort: '-created_at',
            }}
          >
            Partecipazioni
          </Link>
          <span className="sep">/</span>
          <span className="here">
            {pCode} · {p.client_name ?? '—'} → {lCode}
          </span>
        </div>
      </div>

      {/* === HERO === */}
      <div className="hero detail-hero">
        <div className="hero-main">
          <div
            className="title-row"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
            }}
          >
            <span
              className="tag code lot-code"
              style={{
                background: 'var(--ink-1)',
                color: '#fff',
                fontFamily: 'var(--font-mono)',
                padding: '4px 10px',
                borderRadius: 'var(--r-sm)',
                fontSize: 'var(--fs-13)',
                fontWeight: 'var(--fw-semibold)',
              }}
            >
              {pCode}
            </span>
            <span
              className="muted"
              style={{
                fontSize: 'var(--fs-11)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                fontWeight: 'var(--fw-medium)',
              }}
            >
              Partecipazione asta
            </span>
          </div>

          <h1
            className="lot-title"
            style={{
              margin: '6px 0 0',
              fontSize: 'var(--fs-22)',
              fontWeight: 'var(--fw-semibold)',
              lineHeight: 1.15,
              letterSpacing: '-0.012em',
            }}
          >
            <Link
              to="/clienti/$id"
              params={{ id: p.client_id }}
              search={{} as never}
              style={{
                color: 'var(--ink-1)',
                borderBottom: '1px solid var(--accent, #f97316)',
                paddingBottom: 1,
              }}
            >
              {p.client_name ?? '—'}
            </Link>{' '}
            <span
              style={{
                color: 'var(--ink-3)',
                fontSize: 'var(--fs-18)',
                fontWeight: 'var(--fw-regular)',
                margin: '0 6px',
              }}
            >
              →
            </span>{' '}
            <Link
              to="/lotti/$id"
              params={{ id: p.property_id }}
              search={{} as never}
              style={{
                color: 'var(--ink-1)',
                borderBottom: '1px solid var(--accent, #f97316)',
                paddingBottom: 1,
              }}
            >
              {p.property_address ?? 'senza indirizzo'}
            </Link>
          </h1>

          <div
            className="lot-sub muted"
            style={{
              display: 'flex',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
              alignItems: 'center',
              fontSize: 'var(--fs-12)',
              marginTop: 6,
            }}
          >
            <span
              className={`tag tab-${p.tipo_asta}`}
              style={{
                padding: '2px 8px',
                borderRadius: 'var(--r-sm)',
                fontSize: 10,
                fontWeight: 'var(--fw-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                background: 'var(--bg-2)',
                color: 'var(--ink-2)',
              }}
            >
              {p.tipo_asta?.toUpperCase() ?? 'TIPO ?'}
            </span>
            <span className="sep">·</span>
            <span>
              Asta{' '}
              <b style={{ color: 'var(--ink-1)' }}>
                {p.property_data_asta ? fmtDateTime(p.property_data_asta) : '—'}
              </b>
            </span>
          </div>
        </div>

        <div
          className="hero-actions"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {/* Stato badge */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 30,
                padding: '0 12px 0 10px',
                borderRadius: 'var(--r-sm)',
                fontSize: 12,
                fontWeight: 'var(--fw-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                background: statusColor.bg,
                color: statusColor.ink,
                borderLeft: `3px solid ${statusColor.border}`,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'currentColor',
                }}
              />
              {statusLabel}
            </span>

            {/* Sposta stato dropdown */}
            {!readOnly && transitions.length > 0 && (
              <div data-stato-menu style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowStatoMenu((v) => !v)}
                >
                  Sposta stato ▾
                </button>
                {showStatoMenu && (
                  <div
                    role="menu"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      right: 0,
                      background: 'var(--surface)',
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r-md)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                      minWidth: 200,
                      padding: 4,
                      zIndex: 50,
                      fontSize: 'var(--fs-12)',
                    }}
                  >
                    {transitions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => onTransitionStatus(s)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '8px 10px',
                          background: 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderRadius: 'var(--r-sm)',
                          fontSize: 'var(--fs-12)',
                        }}
                        onMouseEnter={(e) => {
                          ;(e.currentTarget as HTMLButtonElement).style.background =
                            'var(--bg-2)'
                        }}
                        onMouseLeave={(e) => {
                          ;(e.currentTarget as HTMLButtonElement).style.background =
                            'transparent'
                        }}
                      >
                        → {STATUS_LABEL[s] ?? s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <button
              type="button"
              className="btn"
              onClick={() => setShowMsgModal(true)}
            >
              💬 Invia msg
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setChatOpen(true)}
              title="Apri chat di team su questa partecipazione"
            >
              🗨️ Chat team
            </button>
            <CommentsButton
              entityType="participation"
              entityId={p.id}
              title={`${p.client_name ?? '—'} → ${p.property_address ?? ''}`.slice(0, 80)}
            />
            <div data-more-menu style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn"
                title="Altre azioni"
                onClick={() => setShowMoreMenu((v) => !v)}
              >
                ⋯
              </button>
              {showMoreMenu && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    right: 0,
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r-md)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                    minWidth: 200,
                    padding: 4,
                    zIndex: 50,
                    fontSize: 'var(--fs-12)',
                  }}
                >
                  <MoreItem
                    icon="🔗"
                    label="Copia link partecipazione"
                    onClick={() => {
                      void navigator.clipboard.writeText(
                        window.location.href,
                      )
                      setShowMoreMenu(false)
                    }}
                  />
                  <MoreItem
                    icon="📤"
                    label="Export PDF dettaglio"
                    onClick={() => {
                      setShowMoreMenu(false)
                      window.print()
                    }}
                  />
                  {!readOnly && (
                    <MoreItem
                      icon="📂"
                      label="Archivia"
                      onClick={() => {
                        onTransitionStatus('archivio')
                        setShowMoreMenu(false)
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* === ANCHOR NAV === */}
      <nav className="anchor-nav">
        <a href="#riepilogo" className="on">
          Riepilogo
        </a>
        <a href="#checklist">
          Checklist
          {p.checklist_total > 0 && (
            <span className="ct">
              {p.checklist_done}/{p.checklist_total}
            </span>
          )}
        </a>
        <a href="#documenti">
          Documenti{docs.length > 0 && <span className="ct">{docs.length}</span>}
        </a>
        <a href="#vault">Vault</a>
        {isAggiudicata && (
          <a href="#post-agg">
            Post-aggiudicazione
            {postAggQuery.data && (
              <span className="ct">
                {
                  postAggQuery.data.steps.filter(
                    (s) => s.status === 'completato',
                  ).length
                }
                /{postAggQuery.data.steps.length}
              </span>
            )}
          </a>
        )}
        <a href="#storico">
          Storico
          {activityQuery.data && (
            <span className="ct">{activityQuery.data.length}</span>
          )}
        </a>
        <div className="nav-end">
          {lastActivity && (
            <span
              className="muted"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 'var(--fs-11)',
              }}
              title={fmtDateTime(lastActivity.occurred_at)}
            >
              <span aria-hidden>🕒</span>
              Aggiornata {relativeFromNow(lastActivity.occurred_at)}
              {lastActivity.user_name && (
                <>
                  {' · '}
                  <strong style={{ color: 'var(--ink-2)' }}>
                    {lastActivity.user_name}
                  </strong>
                </>
              )}
            </span>
          )}
        </div>
      </nav>

      {/* === DETAIL GRID === */}
      <div
        className="detail-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 320px',
          gap: 'var(--sp-3)',
          alignItems: 'start',
        }}
      >
        <div
          className="detail-main"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          <RiepilogoSection p={p} cCode={cCode} lCode={lCode} />
          {/* B2 — Professionisti (delegato + notaio) */}
          <section className="section" id="professionisti">
            <div className="section-head">
              <div className="title">Professionisti</div>
            </div>
            <div className="section-body">
              <ProfessionalPicker
                tipo="delegato"
                value={p.delegato_ref ?? null}
                disabled={readOnly}
                onChange={async (prof) => {
                  await patchMut.mutateAsync({ delegato_id: prof?.id ?? null })
                }}
              />
              <ProfessionalPicker
                tipo="notaio"
                value={p.notaio_ref ?? null}
                disabled={readOnly}
                onChange={async (prof) => {
                  await patchMut.mutateAsync({ notaio_id: prof?.id ?? null })
                }}
              />
            </div>
          </section>
          <ChecklistSection
            p={p}
            readOnly={readOnly}
            participationId={id}
            onToggle={(itemId, isDone) =>
              toggleChecklistMut.mutate({ itemId, isDone })
            }
            onDelete={(itemId) => deleteChecklistMut.mutate(itemId)}
            showAddTask={showAddTask}
            setShowAddTask={setShowAddTask}
            newTaskLabel={newTaskLabel}
            setNewTaskLabel={setNewTaskLabel}
            onAddTask={onAddChecklistItem}
            addPending={addChecklistMut.isPending}
            addError={addChecklistMut.error?.message}
          />
          <DocumentiSection
            docs={docs}
            isLoading={docsQuery.isLoading}
            totalSize={totalSize}
            participationId={id}
            readOnly={readOnly}
          />
          <VaultSection
            entries={vaultListQuery.data ?? []}
            isLoading={vaultListQuery.isPending}
            error={vaultListQuery.error?.message ?? null}
            shown={shown}
            now={now}
            unlockBusy={unlockMut.isPending || getSecretMut.isPending}
            onShow={onShowSecret}
            onHide={onHideSecret}
            onDelete={onDeleteVault}
            canDelete={(ownerId) => canDeleteVault(me, ownerId)}
            readOnly={readOnly}
            showForm={showVaultForm}
            setShowForm={setShowVaultForm}
            vLabel={vLabel}
            setVLabel={setVLabel}
            vKind={vKind}
            setVKind={setVKind}
            vSecret={vSecret}
            setVSecret={setVSecret}
            vUsername={vUsername}
            setVUsername={setVUsername}
            vNote={vNote}
            setVNote={setVNote}
            vaultErr={vaultErr}
            onSubmitVault={onSubmitVault}
            createPending={createVaultMut.isPending}
            onEdit={(entry) => setEditingVault(entry)}
          />
          <EsitoSection
            p={p}
            readOnly={readOnly}
            isPastAuction={isPastAuction}
            esitoMode={esitoMode}
            setEsitoMode={setEsitoMode}
            aggPrezzo={aggPrezzo}
            setAggPrezzo={setAggPrezzo}
            aggData={aggData}
            setAggData={setAggData}
            onSubmitEsito={onSubmitEsito}
            onTransition={onTransitionStatus}
          />
          {isAggiudicata && (
            <>
              {/* B3 — Delegato reused from participation.delegato_id */}
              <section className="section" id="professionisti-post-agg">
                <div className="section-head">
                  <div className="title">Delegato asta</div>
                </div>
                <div className="section-body">
                  <ProfessionalPicker
                    tipo="delegato"
                    value={p.delegato_ref ?? null}
                    disabled={readOnly}
                    onChange={async (prof) => {
                      await patchMut.mutateAsync({ delegato_id: prof?.id ?? null })
                    }}
                  />
                </div>
              </section>
              <PostAggSection
                steps={postAggQuery.data?.steps ?? []}
                isLoading={postAggQuery.isPending}
                error={postAggQuery.error?.message ?? null}
                canWrite={!readOnly && (me?.role === 'supervisore' ||
                  (me?.secondary_roles ?? []).includes('responsabile'))}
                pending={patchPostAggMut.isPending}
                onPatchStep={(stepKey, body) =>
                  patchPostAggMut.mutate({ stepKey, body })
                }
              />
            </>
          )}
          <StoricoSection
            items={activityQuery.data ?? []}
            isLoading={activityQuery.isPending}
            error={activityQuery.error?.message ?? null}
          />
        </div>

        <div
          className="detail-rail"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          <CountdownCard
            dataAsta={p.property_data_asta}
            onApriPartecipazione={() => {
              const el = document.getElementById('checklist')
              if (el) el.scrollIntoView({ behavior: 'smooth' })
            }}
          />
          <NumeriChiaveCard p={p} checklistPct={checklistPct} />
          <ConsulenteCard
            consultantName={p.consultant_name}
            consultantId={p.consultant_id}
            canAssign={
              !readOnly &&
              (me?.role === 'supervisore' ||
                (me?.secondary_roles ?? []).includes('responsabile'))
            }
            onReassign={(cid) => patch({ consultant_id: cid })}
            pending={patchMut.isPending}
          />
          <CronologiaMiniCard
            items={activityQuery.data?.slice(0, 5) ?? []}
            total={activityQuery.data?.length ?? 0}
          />
        </div>
      </div>

      {/* Back button floating */}
      <div style={{ marginTop: 'var(--sp-4)' }}>
        <button
          type="button"
          className="btn sm"
          onClick={() => {
            if (window.history.length > 1) router.history.back()
            else
              void router.navigate({
                to: '/partecipazioni',
                search: {
                  page: 1,
                  page_size: 50,
                  q: '',
                  sort: '-created_at',
                },
              })
          }}
        >
          ← Indietro
        </button>
      </div>

      <SendMessageModal
        open={showMsgModal}
        onClose={() => setShowMsgModal(false)}
        clientId={p.client_id}
        propertyId={p.property_id}
        participationId={p.id}
        allowedCodes={
          p.tipo_asta === 'fisica'
            ? ['doc_part_fisica', 'bollo_telematico']
            : ['doc_part_telematica', 'bollo_telematico']
        }
        context={{
          cliente_nome: p.client_name ?? '',
          indirizzo: p.property_address ?? '',
          data_asta: p.property_data_asta
            ? new Date(p.property_data_asta).toLocaleDateString('it-IT')
            : '',
          ora_asta: p.property_data_asta
            ? new Date(p.property_data_asta).toLocaleTimeString('it-IT', {
                hour: '2-digit',
                minute: '2-digit',
              })
            : '',
          iban_tribunale: 'IT83P0832703399000000310104',
          beneficiario_assegno: '',
          rge: '',
          cauzione_importo:
            p.offerta_prezzo != null
              ? Math.round(p.offerta_prezzo * 0.1).toString()
              : '',
          prezzo_minimo:
            p.offerta_prezzo != null ? p.offerta_prezzo.toString() : '',
          data_scadenza_cauzione: '',
          tribunale: 'Tribunale di Roma',
          impronta_documento: '',
        }}
        phone={p.client_phone ?? null}
        email={p.client_email ?? null}
      />
      <NewChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        contextType="participation"
        participationId={p.id}
        contextLabel={`${p.client_name ?? '—'} → ${p.property_address ?? ''}`.slice(0, 80)}
      />
      {editingVault && (
        <VaultEditModal
          entry={editingVault}
          onClose={() => setEditingVault(null)}
          onSave={(body) => {
            updateVaultMut.mutate(
              { id: editingVault.id, body },
              { onSuccess: () => setEditingVault(null) },
            )
          }}
          pending={updateVaultMut.isPending}
          error={updateVaultMut.error?.message ?? null}
        />
      )}
    </>
  )
}

function VaultEditModal({
  entry,
  onClose,
  onSave,
  pending,
  error,
}: {
  entry: VaultEntry
  onClose: () => void
  onSave: (body: {
    label?: string
    note?: string | null
    username?: string | null
    secret?: string
  }) => void
  pending: boolean
  error: string | null
}) {
  const [label, setLabel] = useState(entry.label)
  const [note, setNote] = useState(entry.note ?? '')
  const [username, setUsername] = useState(entry.username ?? '')
  const [secret, setSecret] = useState('')
  const [rotateSecret, setRotateSecret] = useState(false)

  function handleSave() {
    const body: {
      label?: string
      note?: string | null
      username?: string | null
      secret?: string
    } = {}
    if (label.trim() && label !== entry.label) body.label = label.trim()
    if ((note || '') !== (entry.note ?? ''))
      body.note = note.trim() || null
    if ((username || '') !== (entry.username ?? ''))
      body.username = username.trim() || null
    if (rotateSecret && secret.trim()) body.secret = secret
    if (Object.keys(body).length === 0) {
      onClose()
      return
    }
    onSave(body)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 480,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
            ✎ Modifica credenziale vault
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          Modifica metadati ({entry.kind}). Rotazione secret invalida unlock
          attivo per sicurezza.
        </p>
        <div className="form-field">
          <label>Label</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label>Username (opz.)</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label>Note (opz.)</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div
          className="form-field"
          style={{
            border: '1px dashed var(--line)',
            borderRadius: 'var(--r-sm)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--bg-2)',
          }}
        >
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={rotateSecret}
              onChange={(e) => {
                setRotateSecret(e.target.checked)
                if (!e.target.checked) setSecret('')
              }}
            />
            <span>Ruota secret (re-encrypt + invalida unlock)</span>
          </label>
          {rotateSecret && (
            <input
              type="password"
              placeholder="Nuovo secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="new-password"
              style={{ marginTop: 8 }}
            />
          )}
        </div>
        {error && (
          <div className="error" style={{ fontSize: 'var(--fs-12)' }}>
            {error}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            className="btn secondary"
            onClick={onClose}
            disabled={pending}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn accent"
            onClick={handleSave}
            disabled={
              pending ||
              !label.trim() ||
              (rotateSecret && !secret.trim())
            }
          >
            {pending ? 'Salvataggio…' : 'Salva modifiche'}
          </button>
        </div>
      </div>
    </div>
  )
}

// =============================================================
// SECTIONS
// =============================================================

function MoreItem({
  icon,
  label,
  onClick,
}: {
  icon: string
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 10px',
        background: 'transparent',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        borderRadius: 'var(--r-sm)',
        fontSize: 'var(--fs-12)',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-2)'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <span aria-hidden>{icon}</span>
      {label}
    </button>
  )
}

function RiepilogoSection({
  p,
  cCode,
  lCode,
}: {
  p: ParticipationDetail
  cCode: string
  lCode: string
}) {
  const offertaDelta =
    p.offerta_prezzo != null && (p as { prezzo_base?: number }).prezzo_base
      ? null // backend non espone prezzo_base nella ParticipationDetail
      : null
  void offertaDelta

  return (
    <section className="section" id="riepilogo">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>📋</span>
          Riepilogo
        </div>
      </div>
      <div className="section-body" style={{ padding: 0 }}>
        {/* Split 2-col cliente | lotto */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            background: 'var(--line)',
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              padding: 'var(--sp-3)',
            }}
          >
            <RiepilogoHead
              icon="👤"
              label="Cliente"
              linkTo="/clienti/$id"
              linkParams={{ id: p.client_id }}
            />
            <KvLine k="Nome" v={p.client_name ?? '—'} />
            <KvLine k="Codice" v={cCode} mono />
            {p.client_phone && <KvLine k="Telefono" v={p.client_phone} mono />}
            {p.client_email && <KvLine k="Email" v={p.client_email} mono />}
          </div>
          <div
            style={{
              background: 'var(--surface)',
              padding: 'var(--sp-3)',
            }}
          >
            <RiepilogoHead
              icon="🏠"
              label="Lotto"
              linkTo="/lotti/$id"
              linkParams={{ id: p.property_id }}
            />
            <KvLine k="Indirizzo" v={p.property_address ?? '—'} />
            <KvLine k="Codice" v={lCode} mono />
            {p.property_comune && <KvLine k="Comune" v={p.property_comune} />}
            {p.property_data_asta && (
              <KvLine
                k="Data asta"
                v={fmtDateTime(p.property_data_asta)}
                mono
              />
            )}
          </div>
        </div>
        {/* Asta strip */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1,
            background: 'var(--line)',
            borderTop: '1px solid var(--line)',
          }}
        >
          <AstaStat label="Offerta" value={fmtMoney(p.offerta_prezzo)} />
          <AstaStat
            label="Rilancio min"
            value={fmtMoney(p.rilancio_prev)}
          />
          <AstaStat
            label="Aggiudicato"
            value={
              p.prezzo_aggiudicazione != null
                ? fmtMoney(p.prezzo_aggiudicazione)
                : '—'
            }
            accent={p.prezzo_aggiudicazione != null ? 'ok' : undefined}
          />
          <AstaStat
            label="Tipo asta"
            value={p.tipo_asta?.toUpperCase() ?? '—'}
          />
        </div>
      </div>
    </section>
  )
}

function RiepilogoHead({
  icon,
  label,
  linkTo,
  linkParams,
}: {
  icon: string
  label: string
  linkTo: '/clienti/$id' | '/lotti/$id'
  linkParams: { id: string }
}) {
  return (
    <div
      style={{
        fontSize: 'var(--fs-11)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-caps)',
        color: 'var(--ink-3)',
        fontWeight: 'var(--fw-semibold)',
        paddingBottom: 8,
        borderBottom: '1px solid var(--line)',
        marginBottom: 'var(--sp-2)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span aria-hidden>{icon}</span>
      {label}
      <Link
        to={linkTo}
        params={linkParams}
        search={{} as never}
        style={{
          marginLeft: 'auto',
          textTransform: 'none',
          letterSpacing: 0,
          fontWeight: 'var(--fw-regular)',
          color: 'var(--accent-ink)',
          fontSize: 11,
        }}
      >
        apri scheda →
      </Link>
    </div>
  )
}

function KvLine({
  k,
  v,
  mono,
}: {
  k: string
  v: string
  mono?: boolean
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: 'var(--sp-2)',
        padding: '7px 0',
        borderBottom: '1px solid var(--line)',
        alignItems: 'baseline',
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          fontWeight: 'var(--fw-medium)',
        }}
      >
        {k}
      </span>
      <span
        style={{
          fontSize: 'var(--fs-13)',
          color: 'var(--ink-1)',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
        }}
      >
        {v}
      </span>
    </div>
  )
}

function AstaStat({
  label,
  value,
  accent,
}: {
  label: string
  value: string
  accent?: 'ok' | 'risk'
}) {
  return (
    <div
      style={{ background: 'var(--surface)', padding: '10px var(--sp-3)' }}
    >
      <div
        style={{
          fontSize: 9.5,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'var(--ink-3)',
          fontWeight: 'var(--fw-medium)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-15)',
          fontWeight: 'var(--fw-semibold)',
          color:
            accent === 'ok'
              ? 'var(--ok-ink, #15803d)'
              : accent === 'risk'
                ? 'var(--risk-ink, #b91c1c)'
                : 'var(--ink-1)',
          marginTop: 3,
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
    </div>
  )
}

function ChecklistSection({
  p,
  readOnly,
  participationId,
  onToggle,
  onDelete,
  showAddTask,
  setShowAddTask,
  newTaskLabel,
  setNewTaskLabel,
  onAddTask,
  addPending,
  addError,
}: {
  p: ParticipationDetail
  readOnly: boolean
  participationId: string
  onToggle: (itemId: string, isDone: boolean) => void
  onDelete: (itemId: string) => void
  showAddTask: boolean
  setShowAddTask: (v: boolean) => void
  newTaskLabel: string
  setNewTaskLabel: (v: string) => void
  onAddTask: () => void
  addPending: boolean
  addError?: string
}) {
  const pct =
    p.checklist_total > 0
      ? Math.round((p.checklist_done / p.checklist_total) * 100)
      : 0
  return (
    <section className="section" id="checklist">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>✅</span>
          Checklist
          <span
            className="muted"
            style={{
              marginLeft: 8,
              fontSize: 'var(--fs-11)',
              fontWeight: 'var(--fw-regular)',
            }}
          >
            {p.checklist_done}/{p.checklist_total} · {pct}%
          </span>
        </div>
        {!readOnly && (
          <div className="actions">
            <button
              type="button"
              className="btn sm"
              onClick={() => setShowAddTask(!showAddTask)}
            >
              + Task
            </button>
          </div>
        )}
      </div>
      <div className="section-body" style={{ padding: 0 }}>
        {/* Progress bar */}
        <div
          style={{
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--bg-2)',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-15)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--ink-1)',
            }}
          >
            {p.checklist_done} / {p.checklist_total}
          </span>
          <div
            style={{
              flex: 1,
              height: 6,
              background: 'var(--bg-3, #e5e7eb)',
              borderRadius: 'var(--r-pill)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${pct}%`,
                background:
                  pct === 100 ? 'var(--ok, #16a34a)' : 'var(--accent, #f97316)',
                borderRadius: 'var(--r-pill)',
                transition: 'width 200ms',
              }}
            />
          </div>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-13)',
              color: 'var(--ink-2)',
            }}
          >
            {pct}%
          </span>
        </div>

        {/* Add task form */}
        {showAddTask && !readOnly && (
          <div
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'var(--surface)',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              gap: 'var(--sp-2)',
            }}
          >
            <input
              type="text"
              placeholder="Nuovo task"
              value={newTaskLabel}
              onChange={(e) => setNewTaskLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onAddTask()
              }}
              autoFocus
              style={{
                flex: 1,
                padding: '6px 10px',
                fontSize: 'var(--fs-13)',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--line)',
              }}
            />
            <button
              type="button"
              className="btn sm accent"
              onClick={onAddTask}
              disabled={addPending || !newTaskLabel.trim()}
            >
              {addPending ? '…' : 'Aggiungi'}
            </button>
          </div>
        )}

        {/* Items */}
        {p.checklist.length === 0 ? (
          <div
            className="muted"
            style={{
              padding: 'var(--sp-3)',
              fontSize: 'var(--fs-12)',
              fontStyle: 'italic',
            }}
          >
            Nessun task in checklist.
          </div>
        ) : (
          p.checklist.map((item) => (
            <ChecklistStep
              key={item.id}
              participationId={participationId}
              item={item}
              readOnly={readOnly}
              onToggle={onToggle}
              onDelete={onDelete}
            />
          ))
        )}

        {addError && (
          <div className="error" style={{ margin: 'var(--sp-2) var(--sp-3)' }}>
            {addError}
          </div>
        )}
      </div>
    </section>
  )
}

function ChecklistStep({
  participationId,
  item,
  readOnly,
  onToggle,
  onDelete,
}: {
  participationId: string
  item: ChecklistItem
  readOnly: boolean
  onToggle: (itemId: string, isDone: boolean) => void
  onDelete: (itemId: string) => void
}) {
  const [showUpload, setShowUpload] = useState(false)
  const [docKind, setDocKind] = useState<DocumentKind>('altro')
  const [file, setFile] = useState<File | null>(null)
  const [err, setErr] = useState('')

  const uploadMut = useUploadDocument(participationId)
  const urlMut = useFetchDocumentUrl()

  async function onOpenDoc() {
    if (!item.document_id) return
    setErr('')
    try {
      const res = await urlMut.mutateAsync(item.document_id)
      window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore apertura')
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')
    if (!file) {
      setErr('Selezionare un file')
      return
    }
    uploadMut.mutate(
      {
        file,
        kind: docKind,
        participation_id: participationId,
        checklist_item_id: item.id,
      },
      {
        onSuccess: () => {
          setShowUpload(false)
          setFile(null)
        },
        onError: (e) => setErr(e.message),
      },
    )
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '24px 1fr auto auto',
          gap: 'var(--sp-2)',
          padding: '9px var(--sp-3)',
          borderBottom: '1px solid var(--line)',
          alignItems: 'center',
        }}
      >
        <label
          style={{
            cursor: readOnly ? 'not-allowed' : 'pointer',
            display: 'inline-grid',
            placeItems: 'center',
          }}
        >
          <input
            type="checkbox"
            checked={item.is_done}
            onChange={(e) => onToggle(item.id, e.target.checked)}
            disabled={readOnly}
            style={{
              width: 18,
              height: 18,
              accentColor: 'var(--ok, #16a34a)',
              cursor: readOnly ? 'not-allowed' : 'pointer',
            }}
          />
        </label>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--fs-13)',
              fontWeight: 'var(--fw-medium)',
              color: item.is_done ? 'var(--ink-2)' : 'var(--ink-1)',
              textDecoration: item.is_done ? 'line-through' : 'none',
            }}
          >
            {item.label}
          </div>
          {item.document_id && item.document_filename && (
            <button
              type="button"
              onClick={() => void onOpenDoc()}
              style={{
                marginTop: 2,
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: 'var(--accent-ink)',
                fontSize: 10.5,
                cursor: 'pointer',
              }}
            >
              📎 {item.document_filename}
            </button>
          )}
        </div>
        {!item.document_id && !showUpload && !readOnly && (
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => setShowUpload(true)}
            style={{ padding: '2px 8px', fontSize: 10.5 }}
          >
            📤 Carica
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => onDelete(item.id)}
            style={{
              padding: '2px 8px',
              fontSize: 10.5,
              color: 'var(--risk-ink, #b91c1c)',
            }}
            title="Elimina"
          >
            🗑
          </button>
        )}
      </div>
      {showUpload && !readOnly && (
        <form
          onSubmit={onSubmit}
          style={{
            padding: 'var(--sp-2) var(--sp-3) var(--sp-2) 38px',
            display: 'flex',
            gap: 'var(--sp-1)',
            alignItems: 'center',
            flexWrap: 'wrap',
            background: 'var(--bg-2)',
            borderBottom: '1px solid var(--line)',
            fontSize: 'var(--fs-11)',
          }}
        >
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx"
            style={{ fontSize: 11 }}
          />
          <select
            value={docKind}
            onChange={(e) => setDocKind(e.target.value as DocumentKind)}
            style={{ fontSize: 11, padding: '2px 6px' }}
          >
            {DOCUMENT_KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="btn sm accent"
            disabled={uploadMut.isPending || !file}
            style={{ fontSize: 11 }}
          >
            {uploadMut.isPending ? '…' : 'Invia'}
          </button>
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => setShowUpload(false)}
            style={{ fontSize: 11 }}
          >
            Annulla
          </button>
          {err && (
            <span className="error" style={{ flex: '1 0 100%', fontSize: 11 }}>
              {err}
            </span>
          )}
        </form>
      )}
    </>
  )
}

function DocumentiSection({
  docs,
  isLoading,
  totalSize,
  participationId,
  readOnly,
}: {
  docs: DocumentRow[]
  isLoading: boolean
  totalSize: number
  participationId: string
  readOnly: boolean
}) {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [kind, setKind] = useState<DocumentKind>('altro')
  const [err, setErr] = useState('')
  const uploadMut = useUploadDocument(participationId)
  const urlMut = useFetchDocumentUrl()

  async function onOpen(doc: DocumentRow) {
    try {
      const res = await urlMut.mutateAsync(doc.id)
      window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch {
      /* silent */
    }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setErr('')
    try {
      await uploadMut.mutateAsync({
        file,
        kind,
        participation_id: participationId,
      })
      if (fileRef.current) fileRef.current.value = ''
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore upload')
    }
  }

  return (
    <section className="section" id="documenti">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>📁</span>
          Documenti
          {docs.length > 0 && (
            <span
              className="muted"
              style={{
                marginLeft: 8,
                fontSize: 'var(--fs-11)',
                fontWeight: 'var(--fw-regular)',
              }}
            >
              {docs.length} file · {fmtBytes(totalSize)}
            </span>
          )}
        </div>
        {!readOnly && (
          <div className="actions" style={{ display: 'flex', gap: 'var(--sp-1)' }}>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as DocumentKind)}
              style={{
                fontSize: 'var(--fs-12)',
                padding: '4px 8px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--line)',
              }}
            >
              {DOCUMENT_KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploadMut.isPending}
            >
              {uploadMut.isPending ? '…' : 'Carica'}
            </button>
            <input
              ref={fileRef}
              type="file"
              onChange={onPickFile}
              style={{ display: 'none' }}
              accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx,.zip"
            />
          </div>
        )}
      </div>
      <div className="section-body" style={{ padding: 0 }}>
        {err && (
          <div className="error" style={{ margin: 'var(--sp-2) var(--sp-3)' }}>
            {err}
          </div>
        )}
        {isLoading && (
          <div
            className="muted"
            style={{ padding: 'var(--sp-3)', fontSize: 'var(--fs-12)' }}
          >
            Caricamento…
          </div>
        )}
        {!isLoading && docs.length === 0 && (
          <div
            className="muted"
            style={{
              padding: 'var(--sp-3)',
              fontSize: 'var(--fs-12)',
              fontStyle: 'italic',
            }}
          >
            Nessun documento allegato.
          </div>
        )}
        {docs.map((doc) => (
          <DocRow key={doc.id} doc={doc} onOpen={onOpen} />
        ))}
      </div>
    </section>
  )
}

function DocRow({
  doc,
  onOpen,
}: {
  doc: DocumentRow
  onOpen: (d: DocumentRow) => void
}) {
  const ext = doc.filename.split('.').pop()?.toUpperCase() ?? 'FILE'
  const isPdf = ext === 'PDF'
  const isImg = ['JPG', 'JPEG', 'PNG', 'HEIC'].includes(ext)
  const icon = isPdf ? 'PDF' : isImg ? 'IMG' : ext.slice(0, 3)
  const iconColor = isPdf
    ? 'var(--risk-ink, #b91c1c)'
    : isImg
      ? 'var(--info-ink, #0369a1)'
      : 'var(--ink-3)'

  const flag = (() => {
    const s = doc.extraction_status
    if (s === 'done') return { label: 'OCR✓', color: 'var(--ok-ink, #15803d)' }
    if (s === 'processing')
      return { label: 'OCR…', color: 'var(--accent-ink)' }
    if (s === 'failed')
      return { label: 'errore', color: 'var(--risk-ink, #b91c1c)' }
    if (s === 'pending') return { label: 'pending', color: 'var(--ink-3)' }
    return { label: 'n/a', color: 'var(--ink-4)' }
  })()

  return (
    <div
      onClick={() => onOpen(doc)}
      style={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr 90px 70px 60px',
        gap: 'var(--sp-2)',
        alignItems: 'center',
        padding: '10px var(--sp-3)',
        borderBottom: '1px solid var(--line)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.background = 'var(--bg-2)'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.background = 'transparent'
      }}
    >
      <span
        style={{
          width: 36,
          height: 44,
          background: 'var(--bg-2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-sm)',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: '0.04em',
          color: iconColor,
        }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--fs-13)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--ink-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.filename}
        </div>
        <div
          className="muted"
          style={{ fontSize: 10.5, marginTop: 2 }}
        >
          {doc.kind}
          {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
        </div>
      </div>
      <span
        className="muted"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
        }}
      >
        {fmtDate(doc.uploaded_at)}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          color: 'var(--ink-3)',
          textAlign: 'right',
        }}
      >
        {doc.size_bytes != null ? fmtBytes(doc.size_bytes) : '—'}
      </span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 6px',
          borderRadius: 'var(--r-sm)',
          background:
            flag.color === 'var(--ok-ink, #15803d)'
              ? 'oklch(95% 0.04 145)'
              : 'var(--bg-2)',
          color: flag.color,
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          fontWeight: 'var(--fw-semibold)',
          justifyContent: 'center',
        }}
      >
        {flag.label}
      </span>
    </div>
  )
}

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function VaultSection({
  entries,
  isLoading,
  error,
  shown,
  now,
  unlockBusy,
  onShow,
  onHide,
  onDelete,
  canDelete,
  readOnly,
  showForm,
  setShowForm,
  vLabel,
  setVLabel,
  vKind,
  setVKind,
  vSecret,
  setVSecret,
  vUsername,
  setVUsername,
  vNote,
  setVNote,
  vaultErr,
  onSubmitVault,
  createPending,
  onEdit,
}: {
  entries: VaultEntry[]
  isLoading: boolean
  error: string | null
  shown: Record<string, { plaintext: string; expiresAt: number }>
  now: number
  unlockBusy: boolean
  onShow: (id: string) => void
  onHide: (id: string) => void
  onDelete: (e: VaultEntry) => void
  canDelete: (ownerId: string) => boolean
  readOnly: boolean
  showForm: boolean
  setShowForm: (v: boolean) => void
  vLabel: string
  setVLabel: (v: string) => void
  vKind: VaultKind
  setVKind: (v: VaultKind) => void
  vSecret: string
  setVSecret: (v: string) => void
  vUsername: string
  setVUsername: (v: string) => void
  vNote: string
  setVNote: (v: string) => void
  vaultErr: string
  onSubmitVault: (e: React.FormEvent) => void
  createPending: boolean
  onEdit: (entry: VaultEntry) => void
}) {
  return (
    <section className="section" id="vault">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>🔐</span>
          Vault PEC &amp; PIN
          {entries.length > 0 && (
            <span
              className="muted"
              style={{
                marginLeft: 8,
                fontSize: 'var(--fs-11)',
                fontWeight: 'var(--fw-regular)',
              }}
            >
              {entries.length}{' '}
              {entries.length === 1 ? 'credenziale' : 'credenziali'}
            </span>
          )}
        </div>
        {!readOnly && (
          <div className="actions">
            <button
              type="button"
              className="btn sm"
              onClick={() => setShowForm(!showForm)}
            >
              + Credenziale
            </button>
          </div>
        )}
      </div>
      <div className="section-body" style={{ padding: 0 }}>
        {/* Dark gradient card */}
        <div
          style={{
            background:
              'linear-gradient(135deg, oklch(28% 0.05 60) 0%, oklch(22% 0.04 50) 100%)',
            color: '#fff',
            padding: 'var(--sp-3)',
            margin: 'var(--sp-3)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--sp-2)',
              paddingBottom: 'var(--sp-2)',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--fs-11)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: 'oklch(80% 0.04 60)',
                fontWeight: 'var(--fw-semibold)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ color: 'var(--accent, #f97316)' }}>🔐</span>
              Credenziali trasmissione
            </div>
          </div>

          {isLoading && (
            <div
              style={{
                color: 'oklch(80% 0.04 60)',
                fontSize: 'var(--fs-12)',
              }}
            >
              Caricamento credenziali…
            </div>
          )}
          {error && (
            <div className="error" style={{ marginBottom: 'var(--sp-2)' }}>
              {error}
            </div>
          )}
          {!isLoading && entries.length === 0 && (
            <div
              style={{
                color: 'oklch(80% 0.04 60)',
                fontSize: 'var(--fs-12)',
                fontStyle: 'italic',
              }}
            >
              Nessuna credenziale registrata.
            </div>
          )}
          {entries.map((v) => {
            const disp = shown[v.id]
            const remainingMs = disp ? disp.expiresAt - now : 0
            const remainingSec = Math.max(0, Math.floor(remainingMs / 1000))
            const mm = Math.floor(remainingSec / 60)
            const ss = remainingSec % 60
            return (
              <div
                key={v.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '130px 1fr auto',
                  gap: 'var(--sp-2)',
                  padding: '11px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-caps)',
                    color: 'oklch(72% 0.04 60)',
                    fontWeight: 'var(--fw-medium)',
                  }}
                >
                  {v.label}
                  <div
                    style={{
                      fontSize: 9,
                      color: 'oklch(60% 0.04 60)',
                      textTransform: 'none',
                      letterSpacing: 0,
                      marginTop: 2,
                    }}
                  >
                    {v.kind}
                  </div>
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--fs-13)',
                    color: disp ? '#fff' : 'oklch(80% 0.04 60)',
                    letterSpacing: disp ? 0 : '0.18em',
                    wordBreak: 'break-all',
                  }}
                >
                  {disp ? (
                    <>
                      {disp.plaintext}{' '}
                      <span
                        style={{
                          fontSize: 10,
                          color: 'oklch(80% 0.04 60)',
                          marginLeft: 8,
                        }}
                      >
                        {String(mm).padStart(2, '0')}:
                        {String(ss).padStart(2, '0')}
                      </span>
                    </>
                  ) : (
                    '●●●●●●●●●●●●'
                  )}
                  {v.username && (
                    <div
                      style={{
                        fontSize: 10,
                        color: 'oklch(72% 0.04 60)',
                        marginTop: 2,
                        letterSpacing: 0,
                        fontFamily: 'var(--font-sans)',
                      }}
                    >
                      utente: {v.username}
                    </div>
                  )}
                  {v.note && (
                    <div
                      style={{
                        fontSize: 10,
                        color: 'oklch(72% 0.04 60)',
                        marginTop: 2,
                        letterSpacing: 0,
                        fontFamily: 'var(--font-sans)',
                      }}
                    >
                      {v.note}
                    </div>
                  )}
                </span>
                <span style={{ display: 'flex', gap: 4 }}>
                  {!disp ? (
                    <button
                      type="button"
                      onClick={() => onShow(v.id)}
                      disabled={unlockBusy}
                      style={vBtnStyle(true)}
                    >
                      Mostra
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onHide(v.id)}
                      style={vBtnStyle(false)}
                    >
                      Nascondi
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onEdit(v)}
                      style={vBtnStyle(false)}
                      title="Modifica credenziale"
                    >
                      ✎
                    </button>
                  )}
                  {canDelete(v.owner_id) && (
                    <button
                      type="button"
                      onClick={() => onDelete(v)}
                      style={vBtnStyle(false)}
                      title="Elimina"
                    >
                      🗑
                    </button>
                  )}
                </span>
              </div>
            )
          })}
        </div>

        {showForm && !readOnly && (
          <form
            onSubmit={onSubmitVault}
            style={{
              padding: 'var(--sp-3)',
              borderTop: '1px solid var(--line)',
              display: 'grid',
              gap: 'var(--sp-2)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            }}
          >
            <input
              type="text"
              placeholder="Label (es. PEC studio)"
              value={vLabel}
              onChange={(e) => setVLabel(e.target.value)}
              style={inputStyle}
            />
            <select
              value={vKind}
              onChange={(e) => setVKind(e.target.value as VaultKind)}
              style={inputStyle}
            >
              {VAULT_KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <input
              type="password"
              placeholder="Secret"
              value={vSecret}
              onChange={(e) => setVSecret(e.target.value)}
              autoComplete="new-password"
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Username (opz.)"
              value={vUsername}
              onChange={(e) => setVUsername(e.target.value)}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Note (opz.)"
              value={vNote}
              onChange={(e) => setVNote(e.target.value)}
              style={inputStyle}
            />
            <button
              type="submit"
              className="btn sm accent"
              disabled={createPending}
            >
              {createPending ? '…' : '+ Aggiungi'}
            </button>
            {vaultErr && (
              <div
                className="error"
                style={{ gridColumn: '1 / -1', fontSize: 11 }}
              >
                {vaultErr}
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  )
}

function vBtnStyle(accent: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    background: accent ? 'var(--accent, #f97316)' : 'rgba(255,255,255,0.10)',
    border: `1px solid ${accent ? 'var(--accent, #f97316)' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: 'var(--r-sm)',
    color: '#fff',
    fontSize: 10.5,
    fontWeight: 'var(--fw-medium)',
    cursor: 'pointer',
  }
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 'var(--fs-12)',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--line)',
  background: 'var(--surface)',
}

function EsitoSection({
  p,
  readOnly,
  isPastAuction,
  esitoMode,
  setEsitoMode,
  aggPrezzo,
  setAggPrezzo,
  aggData,
  setAggData,
  onSubmitEsito,
  onTransition,
}: {
  p: ParticipationDetail
  readOnly: boolean
  isPastAuction: boolean
  esitoMode: 'aggiudicata' | 'non_aggiudicata' | null
  setEsitoMode: (v: 'aggiudicata' | 'non_aggiudicata' | null) => void
  aggPrezzo: string
  setAggPrezzo: (v: string) => void
  aggData: string
  setAggData: (v: string) => void
  onSubmitEsito: () => void
  onTransition: (s: string) => void
}) {
  const isAggiudicata = p.status === 'aggiudicata'
  const isPersa = p.status === 'non_aggiudicata'
  const closed = isAggiudicata || isPersa

  return (
    <section className="section" id="esito">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>🏆</span>
          Esito post-asta
        </div>
      </div>
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        {closed ? (
          // Stato chiuso — mostra card colorata con prezzo finale
          <div
            style={{
              padding: 'var(--sp-3)',
              background: isAggiudicata
                ? 'oklch(95% 0.04 145)'
                : 'oklch(96% 0.05 25)',
              border: `1px solid ${isAggiudicata ? 'var(--ok, #16a34a)' : 'var(--risk, #dc2626)'}`,
              borderRadius: 'var(--r-md)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--fs-11)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: isAggiudicata
                  ? 'var(--ok-ink, #15803d)'
                  : 'var(--risk-ink, #b91c1c)',
                fontWeight: 'var(--fw-semibold)',
                marginBottom: 'var(--sp-2)',
              }}
            >
              {isAggiudicata ? '✓ Aggiudicata' : '✗ Non aggiudicata (persa)'}
            </div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--fs-22)',
                fontWeight: 'var(--fw-semibold)',
                color: isAggiudicata
                  ? 'var(--ok-ink, #15803d)'
                  : 'var(--risk-ink, #b91c1c)',
              }}
            >
              {fmtMoney(p.prezzo_aggiudicazione)}
            </div>
            <div
              className="muted"
              style={{ fontSize: 'var(--fs-11)', marginTop: 4 }}
            >
              {isAggiudicata
                ? 'prezzo di aggiudicazione'
                : 'prezzo aggiudicatario (cliente non ha vinto)'}
            </div>
            {p.data_aggiudicazione && (
              <div
                className="muted"
                style={{ fontSize: 'var(--fs-12)', marginTop: 4 }}
              >
                Chiusura: {fmtDate(p.data_aggiudicazione)}
              </div>
            )}
          </div>
        ) : esitoMode && !readOnly ? (
          // Form esito unificato (aggiudicata o persa)
          <div
            style={{
              padding: 'var(--sp-3)',
              background: 'var(--bg-2)',
              borderRadius: 'var(--r-md)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-2)',
              border: `1px solid ${esitoMode === 'aggiudicata' ? 'var(--ok, #16a34a)' : 'var(--risk, #dc2626)'}`,
            }}
          >
            <div
              style={{
                fontSize: 'var(--fs-13)',
                fontWeight: 'var(--fw-semibold)',
              }}
            >
              {esitoMode === 'aggiudicata'
                ? '🏆 Marca aggiudicata'
                : '✗ Marca persa'}
            </div>
            <div
              className="muted"
              style={{ fontSize: 'var(--fs-11)' }}
            >
              {esitoMode === 'aggiudicata'
                ? 'Prezzo finale a cui il cliente ha vinto + data chiusura.'
                : 'Prezzo a cui l\'asta si è chiusa (aggiudicatario) + data. Utile per analisi gap vs offerta cliente.'}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  color: 'var(--ink-3)',
                  fontWeight: 'var(--fw-medium)',
                }}
              >
                Prezzo finale (€) *
              </span>
              <input
                type="number"
                placeholder="184500"
                value={aggPrezzo}
                onChange={(e) => setAggPrezzo(e.target.value)}
                style={inputStyle}
                autoFocus
                min={0}
                step={100}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  color: 'var(--ink-3)',
                  fontWeight: 'var(--fw-medium)',
                }}
              >
                Data chiusura asta *
              </span>
              <input
                type="date"
                value={aggData}
                onChange={(e) => setAggData(e.target.value)}
                style={inputStyle}
                max={new Date().toISOString().split('T')[0]}
              />
            </label>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 4 }}>
              <button
                type="button"
                className={`btn sm ${esitoMode === 'aggiudicata' ? 'accent' : ''}`}
                onClick={onSubmitEsito}
                disabled={
                  !aggPrezzo ||
                  Number(aggPrezzo) <= 0 ||
                  !aggData
                }
                style={
                  esitoMode === 'non_aggiudicata'
                    ? {
                        background: 'var(--risk, #dc2626)',
                        color: '#fff',
                        borderColor: 'var(--risk, #dc2626)',
                      }
                    : undefined
                }
              >
                {esitoMode === 'aggiudicata'
                  ? 'Conferma aggiudicazione'
                  : 'Conferma persa'}
              </button>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  setEsitoMode(null)
                  setAggPrezzo('')
                  setAggData('')
                }}
              >
                Annulla
              </button>
            </div>
          </div>
        ) : (
          // Stato apertto — placeholder + action buttons
          <div
            style={{
              padding: 'var(--sp-3)',
              background: 'var(--bg-2)',
              border: '1px dashed var(--line)',
              borderRadius: 'var(--r-md)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 24,
                color: 'var(--ink-4)',
                marginBottom: 8,
              }}
            >
              🕒
            </div>
            <div
              className="muted"
              style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
            >
              {isPastAuction
                ? 'Asta chiusa — registra esito qui sotto.'
                : `Esito disponibile dopo la chiusura asta (${p.property_data_asta ? fmtDateTime(p.property_data_asta) : 'data da definire'}).`}
            </div>
            {!readOnly && (
              <div
                style={{
                  marginTop: 'var(--sp-3)',
                  display: 'flex',
                  gap: 'var(--sp-2)',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => onTransition('aggiudicata')}
                >
                  🏆 Marca aggiudicata
                </button>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => onTransition('non_aggiudicata')}
                >
                  ✗ Marca persa
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

function PostAggSection({
  steps,
  isLoading,
  error,
  canWrite,
  pending,
  onPatchStep,
}: {
  steps: PostAggStep[]
  isLoading: boolean
  error: string | null
  canWrite: boolean
  pending: boolean
  onPatchStep: (
    stepKey: string,
    body: {
      status?: PostAggStepStatus
      data?: string | null
      note?: string | null
    },
  ) => void
}) {
  const [openStep, setOpenStep] = useState<string | null>(null)
  const completed = steps.filter((s) => s.status === 'completato').length

  return (
    <section className="section" id="post-agg">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>🏛</span>
          Iter post-aggiudicazione
          {steps.length > 0 && (
            <span
              className="muted"
              style={{
                marginLeft: 8,
                fontSize: 'var(--fs-11)',
                fontWeight: 'var(--fw-regular)',
              }}
            >
              {completed}/{steps.length} completati
            </span>
          )}
        </div>
        {!canWrite && (
          <span
            className="muted"
            style={{ fontSize: 'var(--fs-11)' }}
            title="Solo Giulia + Michele possono modificare"
          >
            🔒 read-only
          </span>
        )}
      </div>
      <div className="section-body tight">
        {isLoading && (
          <div className="muted" style={{ padding: 'var(--sp-3)', fontSize: 'var(--fs-12)' }}>
            Caricamento iter…
          </div>
        )}
        {error && (
          <div className="error" style={{ margin: 'var(--sp-3)' }}>
            {error}
          </div>
        )}
        {!isLoading && steps.length === 0 && !error && (
          <div className="muted" style={{ padding: 'var(--sp-3)', fontSize: 'var(--fs-12)' }}>
            Iter non inizializzato.
          </div>
        )}
        {!isLoading && steps.length > 0 && (
          <ol
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {steps.map((step, idx) => {
              const isOpen = openStep === step.key
              const variant =
                step.status === 'completato'
                  ? 'ok'
                  : step.status === 'in_corso'
                    ? 'accent'
                    : 'outline'
              const dotColor =
                variant === 'ok'
                  ? 'var(--ok)'
                  : variant === 'accent'
                    ? 'var(--accent)'
                    : 'var(--ink-4, #d1d5db)'
              return (
                <li
                  key={step.key}
                  style={{
                    borderBottom:
                      idx < steps.length - 1
                        ? '1px solid var(--line)'
                        : 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      padding: 'var(--sp-2) var(--sp-3)',
                      cursor: canWrite ? 'pointer' : 'default',
                    }}
                    onClick={() =>
                      canWrite && setOpenStep(isOpen ? null : step.key)
                    }
                  >
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: dotColor,
                        color: variant === 'outline' ? 'var(--ink-2)' : '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 'var(--fs-11)',
                        fontWeight: 'var(--fw-semibold)',
                        flexShrink: 0,
                      }}
                    >
                      {step.status === 'completato' ? '✓' : idx + 1}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 'var(--fs-13)',
                          fontWeight: 'var(--fw-medium)',
                          color: 'var(--ink-1)',
                        }}
                      >
                        {step.label}
                      </div>
                      {(step.data || step.note) && (
                        <div
                          className="muted"
                          style={{ fontSize: 'var(--fs-11)', marginTop: 2 }}
                        >
                          {step.data && (
                            <span style={{ fontFamily: 'var(--font-mono)' }}>
                              {step.data}
                            </span>
                          )}
                          {step.data && step.note && ' · '}
                          {step.note && (
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'inline-block',
                                maxWidth: 360,
                                verticalAlign: 'bottom',
                              }}
                            >
                              {step.note}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span
                      className={`chip ${variant}`}
                      style={{ fontSize: 'var(--fs-10)' }}
                    >
                      {step.status === 'completato'
                        ? 'Completato'
                        : step.status === 'in_corso'
                          ? 'In corso'
                          : 'Pending'}
                    </span>
                  </div>
                  {isOpen && canWrite && (
                    <PostAggStepEditor
                      step={step}
                      pending={pending}
                      onSave={(body) => {
                        onPatchStep(step.key, body)
                        setOpenStep(null)
                      }}
                      onCancel={() => setOpenStep(null)}
                    />
                  )}
                </li>
              )
            })}
          </ol>
        )}
      </div>
    </section>
  )
}

function PostAggStepEditor({
  step,
  pending,
  onSave,
  onCancel,
}: {
  step: PostAggStep
  pending: boolean
  onSave: (body: {
    status?: PostAggStepStatus
    data?: string | null
    note?: string | null
  }) => void
  onCancel: () => void
}) {
  const [status, setStatus] = useState<PostAggStepStatus>(step.status)
  const [data, setData] = useState<string>(step.data ?? '')
  const [note, setNote] = useState<string>(step.note ?? '')

  function handleSave() {
    const body: {
      status?: PostAggStepStatus
      data?: string | null
      note?: string | null
    } = {}
    if (status !== step.status) body.status = status
    if ((data || '') !== (step.data ?? '')) body.data = data.trim() || null
    if ((note || '') !== (step.note ?? '')) body.note = note.trim() || null
    if (Object.keys(body).length === 0) {
      onCancel()
      return
    }
    onSave(body)
  }

  return (
    <div
      style={{
        padding: 'var(--sp-3)',
        background: 'var(--bg-2)',
        borderTop: '1px solid var(--line)',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--sp-2)',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            color: 'var(--ink-3)',
            fontWeight: 'var(--fw-medium)',
          }}
        >
          Stato
        </span>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as PostAggStepStatus)}
        >
          <option value="pending">Pending</option>
          <option value="in_corso">In corso</option>
          <option value="completato">Completato</option>
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            color: 'var(--ink-3)',
            fontWeight: 'var(--fw-medium)',
          }}
        >
          Data
        </span>
        <input
          type="date"
          value={data}
          onChange={(e) => setData(e.target.value)}
        />
      </label>
      <label
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          gridColumn: '1 / -1',
        }}
      >
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            color: 'var(--ink-3)',
            fontWeight: 'var(--fw-medium)',
          }}
        >
          Note
        </span>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note operative su questo step…"
          style={{ width: '100%' }}
        />
      </label>
      <div
        style={{
          gridColumn: '1 / -1',
          display: 'flex',
          gap: 'var(--sp-2)',
          justifyContent: 'flex-end',
        }}
      >
        <button
          type="button"
          className="btn sm secondary"
          onClick={onCancel}
          disabled={pending}
        >
          Annulla
        </button>
        <button
          type="button"
          className="btn sm accent"
          onClick={handleSave}
          disabled={pending}
        >
          {pending ? 'Salvataggio…' : 'Salva'}
        </button>
      </div>
    </div>
  )
}

function StoricoSection({
  items,
  isLoading,
  error,
}: {
  items: ActivityEvent[]
  isLoading: boolean
  error: string | null
}) {
  return (
    <section className="section" id="storico">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>🕒</span>
          Storico
          {items.length > 0 && (
            <span
              className="muted"
              style={{
                marginLeft: 8,
                fontSize: 'var(--fs-11)',
                fontWeight: 'var(--fw-regular)',
              }}
            >
              {items.length} {items.length === 1 ? 'evento' : 'eventi'}
            </span>
          )}
        </div>
      </div>
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        {isLoading && (
          <div className="muted" style={{ fontSize: 'var(--fs-12)' }}>
            Caricamento…
          </div>
        )}
        {error && <div className="error">{error}</div>}
        {!isLoading && items.length === 0 && (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Nessun evento registrato.
          </div>
        )}
        {items.map((ev) => (
          <TimelineRow key={ev.id} ev={ev} />
        ))}
      </div>
    </section>
  )
}

function TimelineRow({ ev }: { ev: ActivityEvent }) {
  const tone = eventTone(ev.action)
  const dotColor =
    tone === 'ok'
      ? 'var(--ok, #16a34a)'
      : tone === 'accent'
        ? 'var(--accent, #f97316)'
        : tone === 'warn'
          ? 'var(--warn, #f59e0b)'
          : tone === 'info'
            ? 'var(--info, #0ea5e9)'
            : 'var(--ink-3)'
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '12px 1fr',
        gap: 'var(--sp-2)',
        paddingBottom: 'var(--sp-2)',
        marginBottom: 'var(--sp-2)',
        borderBottom: '1px solid var(--line)',
        alignItems: 'flex-start',
        position: 'relative',
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: dotColor,
          marginTop: 5,
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--fs-12)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--ink-1)',
          }}
        >
          {describeEvent(ev)}
        </div>
        <div
          className="muted"
          style={{
            fontSize: 10.5,
            fontFamily: 'var(--font-mono)',
            marginTop: 2,
          }}
        >
          {fmtDateTime(ev.occurred_at)}
          {ev.user_name && <> · {ev.user_name}</>}
        </div>
      </div>
    </div>
  )
}

// =============================================================
// RAIL CARDS
// =============================================================

function CountdownCard({
  dataAsta,
  onApriPartecipazione,
}: {
  dataAsta: string | null | undefined
  onApriPartecipazione: () => void
}) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!dataAsta) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [dataAsta])

  if (!dataAsta) {
    return (
      <aside
        style={{
          padding: 'var(--sp-3)',
          background: 'var(--bg-2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <div
          className="muted"
          style={{ fontSize: 'var(--fs-11), fontStyle: italic' }}
        >
          Data asta da definire
        </div>
      </aside>
    )
  }
  const target = new Date(dataAsta).getTime()
  if (Number.isNaN(target)) return null
  const diff = target - now
  if (diff < 0) {
    return (
      <aside
        style={{
          padding: 'var(--sp-3)',
          background: 'var(--bg-2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <div
          className="muted"
          style={{ fontSize: 'var(--fs-12)' }}
        >
          Asta passata · {fmtDateTime(dataAsta)}
        </div>
      </aside>
    )
  }
  const days = Math.floor(diff / 86_400_000)
  const hours = Math.floor((diff / 3_600_000) % 24)
  const mins = Math.floor((diff / 60_000) % 60)
  const secs = Math.floor((diff / 1000) % 60)
  const dt = new Date(dataAsta)
  const dateStr = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`
  const timeStr = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`

  return (
    <aside
      style={{
        background: 'oklch(20% 0.01 240)',
        color: '#fff',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-3)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'var(--accent, #f97316)',
          opacity: 0.25,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 'var(--fs-10)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--accent, #f97316)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--accent, #f97316)',
          }}
        />
        Asta tra
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 22,
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: '-0.01em',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {dateStr} · {timeStr}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 4,
          paddingTop: 'var(--sp-1)',
          borderTop: '1px solid rgba(255,255,255,0.10)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Digit n={days} u="GG" />
        <Digit n={hours} u="ORE" />
        <Digit n={mins} u="MIN" />
        <Digit n={secs} u="SEC" />
      </div>
      <button
        type="button"
        onClick={onApriPartecipazione}
        style={{
          marginTop: 4,
          padding: '8px 12px',
          background: 'var(--accent, #f97316)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--r-sm)',
          fontWeight: 'var(--fw-semibold)',
          fontSize: 'var(--fs-12)',
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1,
        }}
      >
        Apri checklist
      </button>
    </aside>
  )
}

function Digit({ n, u }: { n: number; u: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '4px 0' }}>
      <div
        style={{
          fontSize: 20,
          fontWeight: 'var(--fw-semibold)',
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          color: '#fff',
          lineHeight: 1.1,
        }}
      >
        {String(n).padStart(2, '0')}
      </div>
      <div
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'oklch(65% 0.01 240)',
          marginTop: 2,
        }}
      >
        {u}
      </div>
    </div>
  )
}

function NumeriChiaveCard({
  p,
  checklistPct,
}: {
  p: ParticipationDetail
  checklistPct: number
}) {
  return (
    <aside
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '10px var(--sp-3)',
          fontSize: 'var(--fs-11)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'var(--ink-3)',
          fontWeight: 'var(--fw-semibold)',
          borderBottom: '1px solid var(--line)',
        }}
      >
        Numeri chiave
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--sp-1)',
          padding: 'var(--sp-3)',
          textAlign: 'center',
        }}
      >
        <Stat value={fmtMoneyShort(p.offerta_prezzo)} label="Offerta" />
        <Stat value={fmtMoneyShort(p.rilancio_prev)} label="Rilancio" />
        <Stat
          value={
            p.prezzo_aggiudicazione != null
              ? fmtMoneyShort(p.prezzo_aggiudicazione)
              : '—'
          }
          label="Aggiudicato"
          accent={p.prezzo_aggiudicazione != null}
        />
        <Stat value={`${checklistPct}%`} label="Checklist" />
        <Stat
          value={p.vault_count > 0 ? String(p.vault_count) : '—'}
          label="Vault"
        />
        <Stat
          value={p.tipo_asta?.slice(0, 4).toUpperCase() ?? '—'}
          label="Tipo"
        />
      </div>
    </aside>
  )
}

function Stat({
  value,
  label,
  accent,
}: {
  value: string
  label: string
  accent?: boolean
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--fs-14)',
          fontWeight: 'var(--fw-semibold)',
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          color: accent ? 'var(--ok-ink, #15803d)' : 'var(--ink-1)',
        }}
      >
        {value}
      </div>
      <div
        className="muted"
        style={{
          fontSize: 9.5,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          fontWeight: 'var(--fw-medium)',
        }}
      >
        {label}
      </div>
    </div>
  )
}

function ConsulenteCard({
  consultantName,
  consultantId,
  canAssign = false,
  onReassign,
  pending = false,
}: {
  consultantName: string | null
  consultantId: string
  canAssign?: boolean
  onReassign?: (consultantId: string) => void
  pending?: boolean
}) {
  const usersQuery = useUsersList()
  const consultants = (usersQuery.data?.items ?? []).filter(
    (u) => u.role === 'consulente',
  )
  if (!consultantName) return null
  return (
    <aside
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '10px var(--sp-3)',
          fontSize: 'var(--fs-11)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'var(--ink-3)',
          fontWeight: 'var(--fw-semibold)',
          borderBottom: '1px solid var(--line)',
        }}
      >
        Consulente
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          padding: 'var(--sp-3)',
        }}
      >
        <div
          className="avatar md"
          style={{
            background: avatarColor(consultantId),
            color: '#fff',
          }}
        >
          {initials(consultantName)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--fs-13)',
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            {consultantName}
          </div>
          <div
            className="muted"
            style={{
              fontSize: 10.5,
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-caps)',
              fontWeight: 'var(--fw-medium)',
            }}
          >
            Consulente lead
          </div>
        </div>
      </div>
      {canAssign && onReassign && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            padding: '10px var(--sp-3)',
            borderTop: '1px solid var(--line)',
          }}
        >
          <span
            className="muted"
            style={{
              fontSize: 'var(--fs-11)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-caps)',
              fontWeight: 'var(--fw-medium)',
            }}
          >
            Riassegna
          </span>
          <select
            value=""
            disabled={pending}
            onChange={(e) => {
              if (e.target.value) onReassign(e.target.value)
            }}
            aria-label="Riassegna consulente partecipazione"
            style={{ fontSize: 'var(--fs-12)', flex: 1, minWidth: 0 }}
          >
            <option value="">Cambia consulente…</option>
            {consultants
              .filter((u) => u.id !== consultantId)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
          </select>
        </div>
      )}
    </aside>
  )
}

function CronologiaMiniCard({
  items,
  total,
}: {
  items: ActivityEvent[]
  total: number
}) {
  return (
    <aside
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '10px var(--sp-3)',
          fontSize: 'var(--fs-11)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'var(--ink-3)',
          fontWeight: 'var(--fw-semibold)',
          borderBottom: '1px solid var(--line)',
        }}
      >
        Cronologia · top 5
      </div>
      <div style={{ padding: 'var(--sp-3)' }}>
        {items.length === 0 ? (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Nessun evento.
          </div>
        ) : (
          items.map((ev) => {
            const tone = eventTone(ev.action)
            const dotColor =
              tone === 'ok'
                ? 'var(--ok, #16a34a)'
                : tone === 'accent'
                  ? 'var(--accent, #f97316)'
                  : tone === 'info'
                    ? 'var(--info, #0ea5e9)'
                    : 'var(--ink-3)'
            return (
              <div
                key={ev.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '10px 1fr',
                  gap: 'var(--sp-2)',
                  paddingBottom: 'var(--sp-2)',
                  marginBottom: 'var(--sp-2)',
                  borderBottom: '1px solid var(--line)',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: dotColor,
                    marginTop: 5,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'var(--fs-11)',
                      fontWeight: 'var(--fw-medium)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {describeEvent(ev)}
                  </div>
                  <div
                    className="muted"
                    style={{
                      fontSize: 9.5,
                      fontFamily: 'var(--font-mono)',
                      marginTop: 1,
                    }}
                  >
                    {fmtDateTime(ev.occurred_at)}
                  </div>
                </div>
              </div>
            )
          })
        )}
        {total > 5 && (
          <div style={{ textAlign: 'center', marginTop: 'var(--sp-1)' }}>
            <a
              href="#storico"
              style={{ fontSize: 10.5, color: 'var(--ink-3)' }}
            >
              ↓ Tutti gli {total} eventi
            </a>
          </div>
        )}
      </div>
    </aside>
  )
}
